/**
 * Ritual Bath Mutation Hooks
 *
 * React Query mutation hooks for ritual bath CRUD operations.
 * Follows TanStack Query v5 patterns with optimistic updates and cache invalidation.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ritualBathQueryKeys,
  ritualBathRepository,
} from "../repositories/ritualBath.repository";
import type {
  AddRitualBathOccurrencePayload,
  AddRitualBathOccurrenceResponse,
  CopyRitualBathPayload,
  CreateRitualBathPayload,
  RitualBath,
  UpdateRitualBathPayload,
} from "../types/ritualBath.types";

// ============================================================================
// Mutation Key Helpers
// ============================================================================

export const ritualBathMutationKeys = {
  create: ["ritual-baths", "create"] as const,
  update: (id?: string) => ["ritual-baths", "update", id] as const,
  copy: ["ritual-baths", "copy"] as const,
  schedule: (id?: string) => ["ritual-baths", "schedule", id] as const,
  delete: (id?: string) => ["ritual-baths", "delete", id] as const,
};

// ============================================================================
// Create Ritual Bath Mutation
// ============================================================================

/**
 * Mutation hook to create a new ritual bath.
 * Invalidates relevant queries on success.
 */
export function useCreateRitualBathMutation() {
  const qc = useQueryClient();

  return useMutation<RitualBath, Error, CreateRitualBathPayload>({
    mutationKey: ritualBathMutationKeys.create,
    mutationFn: (payload) => ritualBathRepository.createRitualBath(payload),

    onSettled: (_data, _error, variables) => {
      const promises: Promise<unknown>[] = [
        qc.invalidateQueries({ queryKey: ritualBathQueryKeys.lists() }),
      ];

      // Invalidate origin-specific queries
      if (variables.origin === "SYSTEM") {
        promises.push(
          qc.invalidateQueries({ queryKey: ritualBathQueryKeys.system() })
        );
      } else if (variables.origin === "MEDIUM") {
        promises.push(
          qc.invalidateQueries({ queryKey: ritualBathQueryKeys.my() })
        );
        if (variables.mediumId) {
          promises.push(
            qc.invalidateQueries({
              queryKey: ritualBathQueryKeys.byMedium(
                String(variables.mediumId)
              ),
            })
          );
        }
      } else if (variables.origin === "TERREIRO" && variables.terreiroId) {
        promises.push(
          qc.invalidateQueries({
            queryKey: ritualBathQueryKeys.byTerreiro(
              String(variables.terreiroId)
            ),
          })
        );
      }

      return Promise.all(promises);
    },

    retry: 0,
  });
}

// ============================================================================
// Update Ritual Bath Mutation
// ============================================================================

interface UpdateRitualBathVariables {
  id: string;
  payload: Partial<UpdateRitualBathPayload>;
}

interface UpdateRitualBathContext {
  previous?: RitualBath | null;
}

/**
 * Mutation hook to update an existing ritual bath.
 * Performs optimistic update and rollback on error.
 */
export function useUpdateRitualBathMutation() {
  const qc = useQueryClient();

  return useMutation<
    RitualBath,
    Error,
    UpdateRitualBathVariables,
    UpdateRitualBathContext
  >({
    mutationKey: ritualBathMutationKeys.update(),
    mutationFn: ({ id, payload }) =>
      ritualBathRepository.updateRitualBath(id, payload),

    onMutate: async ({ id, payload }) => {
      const key = ritualBathQueryKeys.detail(id);
      await qc.cancelQueries({ queryKey: key });

      const previous = qc.getQueryData<RitualBath>(key) ?? null;

      // Optimistic update - ids are UUID strings, never coerce them to numbers
      qc.setQueryData<RitualBath | null>(key, (old) => {
        if (!old) return null;
        const updated: RitualBath = {
          ...old,
          ...payload,
          categoryId: payload.categoryId ?? old.categoryId,
          terreiroId: payload.terreiroId ?? old.terreiroId,
          mediumId: payload.mediumId ?? old.mediumId,
        };
        return updated;
      });

      return { previous };
    },

    onError: (_error, { id }, context) => {
      // Rollback on error
      if (context?.previous) {
        qc.setQueryData(ritualBathQueryKeys.detail(id), context.previous);
      }
    },

    onSuccess: (data, { id }) => {
      // Update cache with server response
      qc.setQueryData(ritualBathQueryKeys.detail(id), data);
    },

    onSettled: (_data, _error, { id }) => {
      // Invalidate related queries
      return Promise.all([
        qc.invalidateQueries({ queryKey: ritualBathQueryKeys.detail(id) }),
        qc.invalidateQueries({ queryKey: ritualBathQueryKeys.lists() }),
        qc.invalidateQueries({ queryKey: ritualBathQueryKeys.system() }),
        qc.invalidateQueries({ queryKey: ritualBathQueryKeys.my() }),
      ]);
    },

    retry: 0,
  });
}

// ============================================================================
// Copy Ritual Bath Mutation
// ============================================================================

/**
 * Mutation hook to copy a ritual bath to a different origin.
 * Used to copy system baths to personal or terreiro collections.
 */
export function useCopyRitualBathMutation() {
  const qc = useQueryClient();

  return useMutation<RitualBath, Error, CopyRitualBathPayload>({
    mutationKey: ritualBathMutationKeys.copy,
    mutationFn: (payload) => ritualBathRepository.copyRitualBath(payload),

    onSettled: (_data, _error, variables) => {
      const promises: Promise<unknown>[] = [
        qc.invalidateQueries({ queryKey: ritualBathQueryKeys.lists() }),
      ];

      // Invalidate target origin queries
      if (variables.targetOrigin === "MEDIUM") {
        promises.push(
          qc.invalidateQueries({ queryKey: ritualBathQueryKeys.my() })
        );
        if (variables.mediumId) {
          promises.push(
            qc.invalidateQueries({
              queryKey: ritualBathQueryKeys.byMedium(
                String(variables.mediumId)
              ),
            })
          );
        }
      } else if (
        variables.targetOrigin === "TERREIRO" &&
        variables.terreiroId
      ) {
        promises.push(
          qc.invalidateQueries({
            queryKey: ritualBathQueryKeys.byTerreiro(
              String(variables.terreiroId)
            ),
          })
        );
      }

      return Promise.all(promises);
    },

    retry: 0,
  });
}

// ============================================================================
// Schedule Ritual Bath (Add Occurrence) Mutation
// ============================================================================

interface AddRitualBathOccurrenceVariables {
  bathId: string;
  payload: AddRitualBathOccurrencePayload;
}

export function useAddRitualBathOccurrenceMutation() {
  const qc = useQueryClient();

  return useMutation<
    AddRitualBathOccurrenceResponse,
    Error,
    AddRitualBathOccurrenceVariables
  >({
    mutationKey: ritualBathMutationKeys.schedule(),
    mutationFn: ({ bathId, payload }) =>
      ritualBathRepository.addRitualBathOccurrence(bathId, payload),

    onSettled: (data) => {
      const promises: Promise<unknown>[] = [
        qc.invalidateQueries({ queryKey: ritualBathQueryKeys.lists() }),
        qc.invalidateQueries({ queryKey: ritualBathQueryKeys.system() }),
        qc.invalidateQueries({ queryKey: ritualBathQueryKeys.my() }),
      ];

      if (data?.bathId) {
        promises.push(
          qc.invalidateQueries({
            queryKey: ritualBathQueryKeys.detail(data.bathId),
          }),
        );
      }

      if (data?.bath.mediumId) {
        promises.push(
          qc.invalidateQueries({
            queryKey: [
              "medium",
              String(data.bath.mediumId),
              "calendar",
              "ritual-baths",
            ],
          }),
        );
      }

      promises.push(
        qc.invalidateQueries({ queryKey: ["calendar"] }),
      );

      return Promise.all(promises);
    },

    retry: 0,
  });
}

// ============================================================================
// Delete Ritual Bath Mutation
// ============================================================================

interface DeleteRitualBathVariables {
  id: string;
}

/**
 * Mutation hook to delete a ritual bath.
 * Removes from cache immediately and invalidates lists.
 */
export function useDeleteRitualBathMutation() {
  const qc = useQueryClient();

  return useMutation<void, Error, DeleteRitualBathVariables>({
    mutationKey: ritualBathMutationKeys.delete(),
    mutationFn: ({ id }) => ritualBathRepository.deleteRitualBath(id),

    onMutate: async ({ id }) => {
      // Remove from detail cache immediately
      await qc.cancelQueries({ queryKey: ritualBathQueryKeys.detail(id) });
      qc.removeQueries({ queryKey: ritualBathQueryKeys.detail(id) });
    },

    onSettled: () => {
      // Invalidate all lists
      return Promise.all([
        qc.invalidateQueries({ queryKey: ritualBathQueryKeys.lists() }),
        qc.invalidateQueries({ queryKey: ritualBathQueryKeys.system() }),
        qc.invalidateQueries({ queryKey: ritualBathQueryKeys.my() }),
      ]);
    },

    retry: 0,
  });
}
